

import { Component, Vue, Watch, Prop } from "vue-property-decorator"

import { DataDefinitionWithFields, FieldInstance } from "@/types"

// @ts-ignore
import draggable from "vuedraggable"
import vSelect from "vue-select"
import { FieldDataTypes, readOnlyFieldTypes } from "@/types/fieldTypes"

export const excludedFieldDataTypes: string[] = [
	...readOnlyFieldTypes,
	FieldDataTypes.ListData,
	FieldDataTypes.ListMultiple,
	FieldDataTypes.UserRole,
	FieldDataTypes.FileOnly,
	FieldDataTypes.TextArea
]

@Component({
	components: {
		draggable,
		"v-select": vSelect
	}
})
export default class DefinitionTitle extends Vue {
	@Prop()
	public definition!: DataDefinitionWithFields;

	@Prop()
	public fields!: any[];

	@Prop()
	public value!: string;

	@Prop({ type: Boolean, default: false })
	public allowEmpty!: boolean

	@Prop({ type: Boolean, default: true })
	public allowFieldRemovals!: boolean

	@Prop({ type: Boolean, default: true })
	public showFieldSelector!: boolean

	@Prop()
	public invalidFeedback!: string;

	@Prop({ type: String, default: "Title Format: " })
	public label!: string;

	@Prop({ type: String, default: "A title format is how to show each item. It's also used for searching when no field is specified" })
	public description!: string;

	@Prop({ type: Boolean, default: false })
	public readonly!: boolean

	@Prop({ type: Boolean, default: false })
	public clearDropdownAfterSelection!: boolean

	@Prop({ type: String, default: "Fields" })
	public fieldSelectPlaceholder!: string

	/* Constants */
	private readonly insertionPlaceholderTerm = "$P"

	/* Data */
	private hasMounted: boolean = false
	renderedValue: string = "";
	startText: string = "";
	fieldDisplay: any = [];

	selectedFieldOption: any | null = null

	// The last input and caret position to be used when adding a field placeholder.
	// If the caret position is not null, then a null lastInputIndex represents the start text
	private lastInputIndex: number | null = null
	private lastCaretPosition: number | null = null

	get definitionTitleFormat () {
		return this.startText + this.fieldDisplay.map((field: any) => "{" + field.id + "}" + field.postText).join("")
	}

	get definitionFields () {
		if (!this.definition) {
			if (this.fields) return this.fields
			return []
		}

		return this.definition.fields.filter(f => f.field.dataType &&
			!excludedFieldDataTypes.includes(f.field.dataType))
	}

	get fieldOptions () {
		return this.definitionFields.map(f => {
			return {
				id: f.field.id,
				label: f.field.title
			}
		})
	}

	private get definitionContainsInsertionPlaceholder (): boolean {
		const definitionTitleFormatSansFields = this.definitionTitleFormat.replace(/\{.+?\}/g, "{}")
		return definitionTitleFormatSansFields.includes(this.insertionPlaceholderTerm)
	}

	mounted () {
		this.setValue(this.value ? this.value : "")

		// avoids emitting an existing value on mount
		Vue.nextTick(() => this.hasMounted = true)
	}

	@Watch("value")
	onValueChanged (value: string) {
		this.setValue(value || "")
	}

	@Watch("definitionFields", { deep: true })
	definitionFieldsChanged (fields: FieldInstance[]) {
		let i = 0
		while (i < this.fieldDisplay.length) {
			const field = this.fieldDisplay[i]
			const matchingFields = fields.filter(f => f.field.id === field.id)
			if (matchingFields.length === 0) {
				this.fieldDisplay.splice(i, 1)

				if (i === 0) {
					this.startText += field.postText
				} else {
					this.fieldDisplay[i - 1].postText += field.postText
				}
			} else {
				field.title = matchingFields[0].field.title
				++i
			}
		}
	}

	setValue (value: string) {
		if (value === this.renderedValue) {
			return
		}
		this.renderedValue = value

		if (this.value) {
			const splitSections = this.value.split("{")

			this.startText = splitSections[0]
			this.fieldDisplay = []

			for (let i = 1; i < splitSections.length; i++) {
				const section = splitSections[i].split("}")
				const fieldId = section[0]
				const field = this.definitionFields.find((f: FieldInstance) => f.field.id === fieldId)

				if (field && field.field) {
					this.fieldDisplay.push({
						id: fieldId,
						title: field.field.title,
						postText: section[1]
					})
				}
			}
		} else {

		}
	}

	@Watch("definitionTitleFormat")
	onTitleFormatChanged (value: string) {
		if (this.hasMounted) {
			this.$emit("input", value)
		}
	}

	fieldStyle (fieldText: string) {
		if (!fieldText || fieldText.length < 2) {
			return {
				"width": "36px"
			}
		}

		return {
			"width": ((fieldText.length + 8) * 6) + "px"
		}
	}

	/**
	 * @summary Adds a field to the title format. If the title format contains the Insertion
	 * Placeholder string, the field will be added at each location it is present in.
	 * Otherwise, it will be inserted at the last caret position, or appended to the end as a fallback.
	 * @param field The field to add to the title format.
	 */
	addField (field: FieldInstance) {
		let elementIndexToFocus: number | null = null
		if (this.definitionContainsInsertionPlaceholder) {
			/* Insert the field at each placeholder instance */

			const updatedFieldDisplay = []

			// handle any Insertion Placeholder instances in the start text
			if (this.startText.includes(this.insertionPlaceholderTerm)) {
				const { remainingText, addedFieldDisplayItems } =
					this.insertFieldAtInsertionPlaceholderInstances(this.startText, field)
				this.startText = remainingText
				updatedFieldDisplay.push(...addedFieldDisplayItems)
			}

			// handle any Insertion Placeholder instances in the field display items
			this.fieldDisplay.forEach((displayField: any) => {
				if (displayField.postText.includes(this.insertionPlaceholderTerm)) {
					const { remainingText, addedFieldDisplayItems } =
						this.insertFieldAtInsertionPlaceholderInstances(displayField.postText, field)
					displayField.postText = remainingText
					updatedFieldDisplay.push(displayField)
					updatedFieldDisplay.push(...addedFieldDisplayItems)
				} else {
					updatedFieldDisplay.push(displayField)
				}
			})

			Vue.set(this, "fieldDisplay", updatedFieldDisplay)
		} else if (this.lastCaretPosition !== null) {
			/* Insert the field at the last caret position */

			// Get the field display item and index for insertion.
			// A null index represents the start text.
			const fieldDisplayIndex = this.lastInputIndex
			const fieldDisplayItem = fieldDisplayIndex !== null
				? this.fieldDisplay[fieldDisplayIndex]
				: null

			// determine how to split the existing text and
			const existingStartText = fieldDisplayItem?.postText ?? this.startText
			const newStartText = existingStartText.substring(0, this.lastCaretPosition)
			const newPostText = existingStartText.substring(this.lastCaretPosition)

			// create the new field display item
			const newDisplayItem = {
				id: field.field.id,
				title: field.field.title,
				postText: newPostText
			}

			// update the start text or the last display item's post text as appropriate
			if (fieldDisplayItem) {
				fieldDisplayItem.postText = newStartText || ""
				this.fieldDisplay.splice(fieldDisplayIndex! + 1, 0, newDisplayItem)
			} else {
				this.startText = newStartText
				this.fieldDisplay.unshift(newDisplayItem)
			}

			elementIndexToFocus = fieldDisplayIndex !== null
				? fieldDisplayIndex + 1
				: 0
		} else {
			/* Append the field to the end as a fallback */

			this.fieldDisplay.push({
				id: field.field.id,
				title: field.field.title,
				postText: ""
			})

			elementIndexToFocus = this.fieldDisplay.length - 1
		}

		if (elementIndexToFocus !== null) {
			Vue.nextTick().then(() => {
				const inputElement = (this.$refs["text-input-" + (elementIndexToFocus)] as HTMLInputElement[])[0]
				inputElement.focus()
				inputElement.setSelectionRange(0, 0)
			})
		}

		this.$emit("field-added", field)
	}

	/**
	 * @summary Generates the data needed to insert a field at each location of the
	 * Insertion Placeholder string in the provided text.
	 * @param text The text to insert the field into. Either the definition's startText,
	 * or the postText of a FieldDisplay item.
	 * @param field The field to insert.
	 * @returns An object containing the remaining text after the last insertion,
	 * and an array of new field display items to be appended after the text.
	 */
	insertFieldAtInsertionPlaceholderInstances (
		text: string, field: FieldInstance
	): { remainingText: string, addedFieldDisplayItems: any[] } {
		const textSplitByPlaceholder = text.split(this.insertionPlaceholderTerm)
		const remainingText = textSplitByPlaceholder.shift()!

		const addedFieldDisplayItems: any[] = []
		textSplitByPlaceholder.forEach(text => {
			addedFieldDisplayItems.push({
				id: field.field.id,
				title: field.field.title,
				postText: text
			})
		})

		return { remainingText, addedFieldDisplayItems }
	}

	addFieldFromSelect (fieldId: string) {
		const field = this.definitionFields.find(f => f.field.id === fieldId)
		if (field) {
			this.addField(field)
		}

		if (this.clearDropdownAfterSelection) {
			Vue.nextTick().then(() => {
				this.selectedFieldOption = null
			})
		}
	}

	removeField (field: any, fieldIndex: number) {
		if (fieldIndex === -1) {
			return
		}

		if (field.postText) {
			if (fieldIndex === 0) {
				this.startText += field.postText
			} else {
				const previousField = this.fieldDisplay[fieldIndex - 1]
				previousField.postText += field.postText
			}
		}

		this.fieldDisplay.splice(fieldIndex, 1)

		this.$emit("field-removed", field)
	}

	async keydown (field: FieldInstance, index: number, event: KeyboardEvent) {
		if (this.readonly) {
			return
		}

		const caretPosition = (event.target! as HTMLInputElement).selectionStart

		if (event.keyCode === 8 && this.allowFieldRemovals) {
			// Backspace

			if (caretPosition === 0) {
				if (field) {
					const prevFieldTextLength = index === 0
						? this.startText.length || 0
						: this.fieldDisplay[index - 1]?.postText.length || 0
					this.removeField(field, index)

					let inputElement: HTMLInputElement | null = null
					if (index === 0) {
						inputElement = this.$refs["text-input"] as HTMLInputElement
					} else {
						const focusIndex = index - 1
						inputElement = (this.$refs["text-input-" + focusIndex] as HTMLInputElement[])[0]
					}

					inputElement.focus()
					event.preventDefault()

					await Vue.nextTick()
					const newSelectionIndex = prevFieldTextLength ?? inputElement.value.length
					inputElement.setSelectionRange(newSelectionIndex, newSelectionIndex)
				}
			}
		} else if (event.keyCode === 46 && this.allowFieldRemovals) {
			// Delete

			if ((!field && caretPosition === this.startText.length) ||
				(field && caretPosition === field.postText!.length)) {
				if (!field) {
					if (this.fieldDisplay.length > 0) {
						this.removeField(this.fieldDisplay[0], 0)
					}
				} else {
					if (this.fieldDisplay.length > 0) {
						this.removeField(this.fieldDisplay[index + 1], index)
					}
				}
			}
		} else if (event.keyCode === 37) {
			// arrow left
			if (caretPosition === 0) {
				let inputElement: HTMLInputElement | null = null
				if (index === 0) {
					inputElement = this.$refs["text-input"] as HTMLInputElement ?? null
				} else {
					const focusIndex = index - 1
					inputElement = this.$refs["text-input-" + focusIndex]
						? (this.$refs["text-input-" + focusIndex] as HTMLInputElement[])[0] as HTMLInputElement
						: null
				}

				if (inputElement) {
					inputElement.focus()
					inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length)
				}

				event.preventDefault()
			}
		} else if (event.keyCode === 39) {
			// arrow right
			if ((!field && caretPosition === this.startText.length) ||
				(field && caretPosition === field.postText!.length)) {
				let inputElement: HTMLInputElement | null = null
				if (index === null) {
					inputElement = this.$refs["text-input-0"]
						? (this.$refs["text-input-0"] as HTMLInputElement[])[0] as HTMLInputElement
						: null
				} else {
					const focusIndex = index + 1
					inputElement = this.$refs["text-input-" + focusIndex]
						? (this.$refs["text-input-" + focusIndex] as HTMLInputElement[])[0] as HTMLInputElement
						: null
				}

				if (inputElement) {
					inputElement.focus()
					inputElement.setSelectionRange(0, 0)
				}

				event.preventDefault()
			}
		}
	}

	private onDefinitionInputBlur (inputIndex: number | null) {
		// store the last input and caret position to be used if a field placeholder is added
		const inputId = inputIndex === null ? "text-input" : "text-input-" + inputIndex
		const inputElement = inputIndex === null
			? this.$refs[inputId] as HTMLInputElement
			: (this.$refs[inputId] as HTMLInputElement[])[0]
		this.lastInputIndex = inputIndex
		this.lastCaretPosition = inputElement?.selectionStart
	}
}

